import { useEffect, useMemo } from 'react'
import styled from '@emotion/styled'
import { MapViewState } from '@deck.gl/core/typed'
import { Colors, Levels, Shapes } from 'src/components/theme'
import { StyleToggle } from 'src/components/map/mapbox/controls/style-toggle'
import { Add } from '@emotion-icons/material/Add'
import { Remove } from '@emotion-icons/material/Remove'
import { Navigation } from '@emotion-icons/material/Navigation'
import { MapStyle, MapStyleConfig } from 'src/components/map-new/types'
import { MapComponentStyleConfigControl } from 'src/components/map/mapbox/controls/style-config'
import { MAP_FLOATING_WIDGET_SPACING } from 'src/components/theme/mapbox'
import { NO_PRINT_REPORTS } from 'src/utils/html-to-image'
import { useRecoilState } from 'recoil'

import * as mainMapAtoms from 'src/atoms/main-map'
import { AerialBanner, AerialToggle } from 'src/components/map/google-map/aerial-toggle'
import { AERIAL_TOGGLE_ZOOM_THRESHOLD } from 'src/components/map/google-map/map-type-toggle'

type MapComponentControlsProps = {
  enabled: boolean
  disableZoomControl?: boolean
  viewState: MapViewState
  mapStyle: MapStyle
  zoom: number
  onMapStyleChange: (mapStyle: MapStyle) => void
  mapStyleConfig: MapStyleConfig
  onMapStyleConfigChange: (mapStyleConfig: MapStyleConfig) => void
  onZoomIn: () => void
  onZoomOut: () => void
  on3dReset: () => void
}

const MapControls = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  z-index: ${Levels.mapLayerControl};
  position: absolute;
  right: ${MAP_FLOATING_WIDGET_SPACING}px;
  bottom: ${MAP_FLOATING_WIDGET_SPACING}px;
`

export const MapControlSet = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Shapes.spacing[2]}px;
  justify-content: flex-end;
  align-items: flex-end;
  margin-left: ${Shapes.spacing[2]}px;
`

export const MapLayerOption = styled(MapControlSet)`
  margin-left: 0px;
`

const SIZE = 32
const MapControl = styled.div`
  user-select: none;
  box-shadow: rgb(0 0 0 / 30%) 0px 1px 4px -1px;
  border-radius: ${Shapes.borderRadius}px;
  cursor: pointer;
  background-color: ${Colors.bgWhite};
  width: ${SIZE}px;
`

const ThreeDControl = styled(MapControl)`
  height: ${SIZE}px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid ${Colors.border};
  margin-bottom: ${Shapes.spacing[3]}px;
`

const BottomRightControls = styled.div`
  display: flex;
  align-items: flex-end;
  margin-top: ${Shapes.spacing[2]}px;
`

const ZoomControl = styled(MapControl)`
  height: ${SIZE * 2}px;
  outline: 1px solid ${Colors.border};
  outline-offset: -1px;
`

const ZoomInControl = styled.div`
  width: ${SIZE}px;
  height: ${SIZE}px;
  display: flex;
  justify-content: center;
  align-items: center;
`

const ZoomOutControl = styled(ZoomInControl)`
  border-top: 1px solid ${Colors.border};
`

export const MapComponentControls = (props: MapComponentControlsProps) => {
  const {
    zoom,
    enabled,
    viewState,
    mapStyle,
    onMapStyleChange,
    onZoomIn,
    onZoomOut,
    on3dReset,
    mapStyleConfig,
    onMapStyleConfigChange,
    disableZoomControl = false,
  } = props

  const [isAerialEnabled, setIsAerialEnbaled] = useRecoilState(mainMapAtoms.isAerialEnabled)
  const showAerial = useMemo(
    () => (mapStyle === MapStyle.SATELLITE && zoom && zoom >= AERIAL_TOGGLE_ZOOM_THRESHOLD) || isAerialEnabled,
    [mapStyle, zoom]
  )

  useEffect(() => {
    const showableAerial = mapStyle === MapStyle.SATELLITE && zoom && zoom >= AERIAL_TOGGLE_ZOOM_THRESHOLD
    if (!showableAerial && isAerialEnabled) {
      setIsAerialEnbaled(false)
    }
  }, [mapStyle, zoom, isAerialEnabled])

  if (!enabled) {
    return null
  }

  const reset3dActive = viewState.bearing !== 0 || viewState.pitch !== 0
  // Reverse Bearing to make the icon point towards the North
  const reset3dTransform = `rotateX(${viewState.pitch || 0}deg) rotateZ(${(viewState.bearing || 0) * -1}deg)`

  return (
    <>
      {isAerialEnabled && <AerialBanner />}

      <MapControls className={`MapControls ${NO_PRINT_REPORTS}`}>
        {reset3dActive && (
          <ThreeDControl onClick={on3dReset}>
            <Navigation size={16} style={{ transform: reset3dTransform }} />
          </ThreeDControl>
        )}
        <MapComponentStyleConfigControl
          className="MapComponentStyleConfigControl"
          mapStyleConfig={mapStyleConfig}
          onMapStyleConfigChange={onMapStyleConfigChange}
        />
        {showAerial && <AerialToggle />}

        <BottomRightControls>
          <MapControlSet>
            <StyleToggle mapStyle={mapStyle} onMapStyleChange={onMapStyleChange} />
          </MapControlSet>
          <MapControlSet style={{ pointerEvents: disableZoomControl ? 'none' : 'auto' }}>
            <ZoomControl>
              <ZoomInControl onClick={onZoomIn}>
                <Add size={20} />
              </ZoomInControl>
              <ZoomOutControl onClick={onZoomOut}>
                <Remove size={20} />
              </ZoomOutControl>
            </ZoomControl>
          </MapControlSet>
        </BottomRightControls>
      </MapControls>
    </>
  )
}
